/**
 * The parts of a HEIF that the readers of this app need (1.187.0, review
 * 2026-10b C2): the boxes of `meta`, its items (`iinf`), their references
 * (`iref`), their properties (`iprp`) and where their bytes lie (`iloc`).
 *
 * **One reader for three questions.** `exif.ts` looked for the Exif item
 * with its own walk through the same boxes, `heifGainMap.ts` (1.181.0) for
 * the gain map, and `colourProfile.ts` searched the first 4 MB byte by byte
 * for `colr`. The version cases of `infe` and `iloc` were written twice.
 * The boxes themselves are walked by `mp4Boxes.ts` (ISO/IEC 14496-12 is the
 * same box format).
 */
import { boxesIn, boxesWhile, typeAt, type Box } from './mp4Boxes'

export interface Item {
  id: number
  type: string
  /** Where the low byte of the `infe` flags lies: bit 0 says "hidden". */
  flagsAt: number
}

/** The children of a box, behind its header and, for a full box, its version and flags. */
export function inside(view: DataView, box: Box, full: boolean): Box[] {
  return boxesIn(view, box.start + box.header + (full ? 4 : 0), box.start + box.size) ?? []
}

export const versionOf = (view: DataView, box: Box): number => view.getUint8(box.start + box.header)
export const flagsOf = (view: DataView, box: Box): number => view.getUint32(box.start + box.header) & 0xffffff

export function cString(view: DataView, at: number, end: number): { text: string; next: number } {
  let i = at
  let text = ''
  while (i < end && view.getUint8(i) !== 0) text += String.fromCharCode(view.getUint8(i++))
  return { text, next: i + 1 }
}

/** Read an unsigned number of 0, 2, 4 or 8 bytes. */
export function uint(view: DataView, at: number, size: number): number {
  if (size === 0) return 0
  if (size === 2) return view.getUint16(at)
  if (size === 4) return view.getUint32(at)
  return view.getUint32(at) * 2 ** 32 + view.getUint32(at + 4)
}

export function itemsOf(view: DataView, iinf: Box): Item[] {
  const at = iinf.start + iinf.header
  const skip = versionOf(view, iinf) === 0 ? 2 : 4
  const out: Item[] = []
  for (const infe of boxesIn(view, at + 4 + skip, iinf.start + iinf.size) ?? []) {
    if (infe.type !== 'infe') continue
    const v = versionOf(view, infe)
    if (v < 2) continue
    let p = infe.start + infe.header + 4
    const id = v === 2 ? view.getUint16(p) : view.getUint32(p)
    p += (v === 2 ? 2 : 4) + 2
    out.push({ id, type: typeAt(view, p), flagsAt: infe.start + infe.header + 3 })
  }
  return out
}

/** Item references by kind: `[from, to[]]`. */
export function refsOf(view: DataView, iref: Box): { kind: string; from: number; to: number[]; at: number }[] {
  const wide = versionOf(view, iref) !== 0
  const w = wide ? 4 : 2
  return inside(view, iref, true).map((r) => {
    let p = r.start + r.header
    const from = uint(view, p, w)
    p += w
    const count = view.getUint16(p)
    p += 2
    const to: number[] = []
    for (let k = 0; k < count; k++) to.push(uint(view, p + k * w, w))
    return { kind: r.type, from, to, at: r.start + 4 }
  })
}

/** For each item, the property boxes associated with it. */
export function propertiesOf(view: DataView, iprp: Box): Map<number, Box[]> {
  const kids = inside(view, iprp, false)
  const ipco = kids.find((b) => b.type === 'ipco')
  const ipma = kids.find((b) => b.type === 'ipma')
  const out = new Map<number, Box[]>()
  if (ipco === undefined || ipma === undefined) return out
  const props = inside(view, ipco, false)
  const v = versionOf(view, ipma)
  const wideIndex = (flagsOf(view, ipma) & 1) === 1
  let p = ipma.start + ipma.header + 4
  const count = view.getUint32(p)
  p += 4
  for (let e = 0; e < count; e++) {
    const id = v < 1 ? view.getUint16(p) : view.getUint32(p)
    p += v < 1 ? 2 : 4
    const n = view.getUint8(p++)
    const list: Box[] = []
    for (let k = 0; k < n; k++) {
      const index = wideIndex ? view.getUint16(p) & 0x7fff : view.getUint8(p) & 0x7f
      p += wideIndex ? 2 : 1
      const prop = props[index - 1]
      if (prop !== undefined) list.push(prop)
    }
    out.set(id, list)
  }
  return out
}

/** Where the bytes of an item lie, extent by extent, from `iloc`; null for a construction this code does not read. */
export function itemExtents(view: DataView, meta: Box[], id: number): { from: number; length: number }[] | null {
  const iloc = meta.find((b) => b.type === 'iloc')
  const idat = meta.find((b) => b.type === 'idat')
  if (iloc === undefined) return null
  const v = versionOf(view, iloc)
  let p = iloc.start + iloc.header + 4
  const offsetSize = view.getUint8(p) >> 4
  const lengthSize = view.getUint8(p) & 15
  const baseSize = view.getUint8(p + 1) >> 4
  const indexSize = v === 1 || v === 2 ? view.getUint8(p + 1) & 15 : 0
  p += 2
  const count = v < 2 ? view.getUint16(p) : view.getUint32(p)
  p += v < 2 ? 2 : 4
  for (let i = 0; i < count && p < iloc.start + iloc.size; i++) {
    const item = v < 2 ? view.getUint16(p) : view.getUint32(p)
    p += v < 2 ? 2 : 4
    let method = 0
    if (v === 1 || v === 2) {
      method = view.getUint16(p) & 15
      p += 2
    }
    p += 2 // data reference index
    const base = uint(view, p, baseSize)
    p += baseSize
    const extents = view.getUint16(p)
    p += 2
    const parts: { from: number; length: number }[] = []
    for (let e = 0; e < extents; e++) {
      p += indexSize
      const offset = uint(view, p, offsetSize)
      p += offsetSize
      const length = uint(view, p, lengthSize)
      p += lengthSize
      parts.push({ from: (method === 1 && idat !== undefined ? idat.start + idat.header : 0) + base + offset, length })
    }
    if (item !== id) continue
    if (method !== 0 && method !== 1) return null
    return parts
  }
  return null
}

/** The bytes of an item from `iloc`, from the file or from `idat`. */
export function itemData(bytes: Uint8Array, view: DataView, meta: Box[], id: number): Uint8Array | null {
  const parts = itemExtents(view, meta, id)
  if (parts === null) return null
  const out = new Uint8Array(parts.reduce((n, x) => n + x.length, 0))
  let at = 0
  for (const x of parts) {
    out.set(bytes.subarray(x.from, x.from + x.length), at)
    at += x.length
  }
  return out
}

/**
 * The `meta` of a HEIF: its view, its boxes and the primary item. The top
 * level is read as far as it adds up -- `meta` stands before the picture
 * data, and a file cut behind it still tells its items.
 */
export function heifMeta(bytes: Uint8Array): { view: DataView; metaBox: Box; meta: Box[]; primary: number | null } | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const metaBox = boxesWhile(view, 0, bytes.length).find((b) => b.type === 'meta')
  if (metaBox === undefined) return null
  const meta = inside(view, metaBox, true)
  const pitm = meta.find((b) => b.type === 'pitm')
  const primary = pitm === undefined
    ? null
    : versionOf(view, pitm) === 0 ? view.getUint16(pitm.start + pitm.header + 4) : view.getUint32(pitm.start + pitm.header + 4)
  return { view, metaBox, meta, primary }
}
